import { ClaimTransactionModel, common, crypto, InvocationTransactionModel, ScriptBuilder, utils, WitnessModel, } from '@neo-one/client-common';
import { processActionsAndMessage } from '@neo-one/client-switch';
import { utils as commonUtils } from '@neo-one/utils';
import { InvokeError, NothingToClaimError, NothingToTransferError, UnknownAccountError } from '../errors';
import { UserAccountProviderBase, } from './UserAccountProviderBase';
export class LocalUserAccountProvider extends UserAccountProviderBase {
    constructor({ keystore, provider }) {
        super({ provider });
        this.keystore = keystore;
        this.currentUserAccount$ = keystore.currentUserAccount$;
        this.userAccounts$ = keystore.userAccounts$;
        this.networks$ = provider.networks$;
        const deleteUserAccountIn = this.keystore.deleteUserAccount;
        if (deleteUserAccountIn !== undefined) {
            const deleteUserAccount = deleteUserAccountIn.bind(this.keystore);
            this.deleteUserAccount = async (id) => {
                await deleteUserAccount(id);
            };
        }
        const updateUserAccountNameIn = this.keystore.updateUserAccountName;
        if (updateUserAccountNameIn !== undefined) {
            const updateUserAccountName = updateUserAccountNameIn.bind(this.keystore);
            this.updateUserAccountName = async (options) => {
                await updateUserAccountName(options);
            };
        }
        const iterActionsRaw = this.provider.iterActionsRaw;
        if (iterActionsRaw !== undefined) {
            this.iterActionsRaw = iterActionsRaw.bind(this.keystore);
        }
        this.executeInvokeMethod = this.executeInvoke;
        this.executeInvokeScript = this.executeInvoke;
    }
    getCurrentUserAccount() {
        return this.keystore.getCurrentUserAccount();
    }
    getUserAccounts() {
        return this.keystore.getUserAccounts();
    }
    getNetworks() {
        return this.provider.getNetworks();
    }
    async selectUserAccount(id) {
        await this.keystore.selectUserAccount(id);
    }
    async sendTransaction({ inputs, transaction: transactionUnsignedIn, from, onConfirm, networkFee, sourceMaps, }) {
        return this.capture(async () => {
            let transactionUnsigned = transactionUnsignedIn;
            if (this.keystore.byteLimit !== undefined) {
                transactionUnsigned = await this.consolidate({
                    inputs,
                    from,
                    transactionUnsignedIn,
                    byteLimit: this.keystore.byteLimit,
                });
            }
            const address = from.address;
            if (transactionUnsigned.inputs.length === 0 &&
                (transactionUnsigned.claims == undefined ||
                    !Array.isArray(transactionUnsigned.claims) ||
                    transactionUnsigned.claims.length === 0)) {
                transactionUnsigned = transactionUnsigned.clone({
                    attributes: transactionUnsigned.attributes.concat(this.convertAttributes([
                        {
                            usage: 'Script',
                            data: address,
                        },
                    ])),
                });
            }
            const [signature, inputOutputs, claimOutputs] = await Promise.all([
                this.keystore.sign({
                    account: from,
                    message: transactionUnsigned.serializeUnsigned().toString('hex'),
                }),
                Promise.all(transactionUnsigned.inputs.map(async (input) => this.provider.getOutput(from.network, { hash: common.uInt256ToString(input.hash), index: input.index }))),
                transactionUnsigned instanceof ClaimTransactionModel
                    ? Promise.all(transactionUnsigned.claims.map(async (input) => this.provider.getOutput(from.network, {
                        hash: common.uInt256ToString(input.hash),
                        index: input.index,
                    })))
                    : Promise.resolve([]),
            ]);
            const userAccount = this.getUserAccount(from);
            const witness = crypto.createWitnessForSignature(Buffer.from(signature, 'hex'), common.stringToECPoint(userAccount.publicKey), WitnessModel);
            const result = await this.provider.relayTransaction(from.network, this.addWitness({
                transaction: transactionUnsigned,
                inputOutputs: inputOutputs.concat(claimOutputs),
                address,
                witness,
            }), networkFee);
            const failures = result.verifyResult === undefined
                ? []
                : result.verifyResult.verifications.filter(({ failureMessage }) => failureMessage !== undefined);
            if (failures.length > 0) {
                const message = await processActionsAndMessage({
                    actions: failures.reduce((acc, { actions }) => acc.concat(actions), []),
                    message: failures
                        .map(({ failureMessage }) => failureMessage)
                        .filter(commonUtils.notNull)
                        .join(' '),
                    sourceMaps,
                });
                throw new InvokeError(message);
            }
            transactionUnsigned.inputs.forEach((transfer) => this.mutableUsedOutputs.add(`${common.uInt256ToString(transfer.hash)}:${transfer.index}`));
            const { transaction } = result;
            return {
                transaction: transaction,
                confirmed: async (optionsIn = {}) => {
                    const options = {
                        ...optionsIn,
                        timeoutMS: optionsIn.timeoutMS === undefined ? 120000 : optionsIn.timeoutMS,
                    };
                    const receipt = await this.provider.getTransactionReceipt(from.network, transaction.hash, options);
                    return onConfirm({ transaction, receipt });
                },
            };
        }, {
            name: 'neo_send_transaction',
        });
    }
    async executeInvokeClaim({ contract, inputs, outputs, unclaimed, amount, attributes, method, params, paramsZipped, from, sourceMaps, }) {
        const transaction = new ClaimTransactionModel({
            inputs: this.convertInputs(inputs),
            claims: this.convertInputs(unclaimed),
            outputs: this.convertOutputs(outputs.concat([
                {
                    address: contract,
                    asset: common.GAS_ASSET_HASH,
                    value: amount,
                },
            ])),
            attributes: this.convertAttributes(attributes.concat(this.getInvokeAttributes(contract, method, paramsZipped, false, from.address))),
            scripts: this.getInvokeScripts(method, params, true),
        });
        return this.sendTransaction({
            inputs,
            from,
            transaction,
            onConfirm: async ({ receipt }) => receipt,
            sourceMaps,
        });
    }
    async executeTransfer(transfers, from, attributes, networkFee) {
        const { inputs, outputs } = await this.getTransfersInputOutputs({
            transfers: transfers.map((transfer) => ({ from, ...transfer })),
            from,
            gas: networkFee,
        });
        if (inputs.length === 0 && this.keystore.byteLimit === undefined) {
            throw new NothingToTransferError();
        }
        const transaction = new InvocationTransactionModel({
            inputs: this.convertInputs(inputs),
            outputs: this.convertOutputs(outputs),
            attributes: this.convertAttributes(attributes),
            gas: utils.ZERO,
            script: new ScriptBuilder().emitOp('PUSH1').build(),
        });
        return this.sendTransaction({
            from,
            transaction,
            inputs,
            onConfirm: async ({ receipt }) => receipt,
            networkFee,
        });
    }
    async executeClaim(from, attributes, networkFee) {
        const [{ unclaimed, amount }, { inputs, outputs }] = await Promise.all([
            this.provider.getUnclaimed(from.network, from.address),
            this.getTransfersInputOutputs({
                from,
                gas: networkFee,
                transfers: [],
            }),
        ]);
        if (unclaimed.length === 0) {
            throw new NothingToClaimError(from);
        }
        const transaction = new ClaimTransactionModel({
            inputs: this.convertInputs(inputs),
            claims: this.convertInputs(unclaimed),
            outputs: this.convertOutputs(outputs.concat([
                {
                    address: from.address,
                    asset: common.GAS_ASSET_HASH,
                    value: amount,
                },
            ])),
            attributes: this.convertAttributes(attributes),
        });
        return this.sendTransaction({
            inputs,
            from,
            transaction,
            onConfirm: async ({ receipt }) => receipt,
            networkFee,
        });
    }
    async executeInvoke({ script, from, attributes, inputs, outputs, rawInputs, rawOutputs, gas, scripts, reorderOutputs, onConfirm, sourceMaps, }) {
        const invokeTransaction = new InvocationTransactionModel({
            version: 1,
            inputs: this.convertInputs(rawInputs.concat(inputs)),
            outputs: this.convertOutputs(reorderOutputs(rawOutputs.concat(outputs))),
            attributes: this.convertAttributes(attributes),
            gas: utils.bigNumberToBN(gas, 8),
            script,
            scripts,
        });
        try {
            const result = await this.sendTransaction({
                from,
                inputs,
                transaction: invokeTransaction,
                onConfirm: async ({ transaction, receipt }) => {
                    const data = await this.provider.getInvocationData(from.network, transaction.hash);
                    return onConfirm({ transaction, receipt, data });
                },
                sourceMaps,
            });
            return result;
        }
        catch (error) {
            const message = await processActionsAndMessage({
                actions: [],
                message: error.message,
                sourceMaps,
            });
            if (message === error.message) {
                throw error;
            }
            throw new Error(message);
        }
    }
    getUserAccount(id) {
        const userAccount = this.keystore
            .getUserAccounts()
            .find((account) => account.id.network === id.network && account.id.address === id.address);
        if (userAccount === undefined) {
            throw new UnknownAccountError(id.address);
        }
        return userAccount;
    }
}

//# sourceMappingURL=LocalUserAccountProvider.js.map
